<template>
  <template v-if="!loading && profileLoaded">
    <component
      v-if="component"
      v-bind:is="component" />
    <error
      v-else
      no_return_home />
  </template>
  <template v-else>
    <div class="w-full lg:h-full">
      <spinner
        sizes="w-20 h-20"
        thickness="border-8" />
    </div>
  </template>
</template>
<script setup lang="ts">
  import spinner from '@/components/utils/spinner.vue';
  import error from '@/views/errors/error-404.vue';
  import InscriptionAccueil from '@/views/_espace/_refonte/participant/accueil.vue';
  import InscriptionFormations from '@/views/_espace/_refonte/participant/formations.vue';
  import InscriptionFormationsDetails from '@/views/_espace/_refonte/participant/formations-details.vue';
  import InscriptionFormationsCursusDetails from '@/views/_espace/_refonte/participant/formations-details-cursus.vue';
  import InscriptionFormationsDetailsElearning from '@/views/_espace/_refonte/participant/formations-details-elearning.vue';
  import GestionAccueil from '@/views/_espace/_refonte/gestion/accueil.vue';
  import GestionInscriptions from '@/views/_espace/_refonte/gestion/inscriptions.vue';
  import GestionInscriptionsSession from '@/views/_espace/_refonte/gestion/inscriptions-details.vue';
  import GestionInscriptionsCursus from '@/views/_espace/_refonte/gestion/inscriptions-details-cursus.vue';
  import GestionInscriptionsElearning from '@/views/_espace/_refonte/gestion/inscriptions-details-elearning.vue';
  import GestionSessions from '@/views/_espace/_refonte/gestion/sessions.vue';
  import GestionSessionsDetails from '@/views/_espace/_refonte/gestion/sessions-details.vue';
  import GestionSessionsCursusSessionDetails from '@/views/_espace/_refonte/gestion/sessions-cursus-session-details.vue';
  import GestionSessionsCursusElearningDetails from '@/views/_espace/_refonte/gestion/sessions-cursus-elearning-details.vue';
  import GestionFactures from '@/views/_espace/_refonte/gestion/factures.vue';
  import GestionCommandes from '@/views/_espace/_refonte/gestion/commandes.vue';
  import GestionCommandesDetails from '@/views/_espace/_refonte/gestion/commandes-details.vue';
  import GestionInterlocuteurs from '@/views/_espace/_refonte/gestion/interlocuteurs.vue';
  import GestionEtablissements from '@/views/_espace/_refonte/gestion/etablissements.vue';
  import GestionAnnuaire from '@/views/_espace/_refonte/gestion/annuaire.vue';
  import GestionSatisfaction from '@/views/_espace/_refonte/gestion/satisfaction.vue';
  import GestionContrats from '@/views/_espace/_refonte/gestion/contrats.vue';
  import GestionEtablissementsDetails from '@/views/_espace/_refonte/gestion/etablissements-details.vue';
  import { computed, onMounted, ref, Ref, watch } from 'vue';
  import { useArborescenceStore } from '@/stores/arborescence';
  import { useAccountStore } from '@/stores/account';
  import { useEntrepriseStore } from '@/stores/entreprise';
  import { useRoute, useRouter } from 'vue-router';
  import { useCatalogueStore } from '@/stores/catalogue';
  import { storeToRefs } from 'pinia';
  import { Entreprise } from '@/types/gestion/entreprise';
  const props = withDefaults(
    defineProps<{
      space_id: string;
      sub_space_id: string;
    }>(),
    {}
  );

  const components: {
    [key: string]: any;
  } = {
    InscriptionAccueil,
    InscriptionFormations,
    InscriptionFormationsDetails,
    InscriptionFormationsCursusDetails,
    InscriptionFormationsDetailsElearning,
    GestionAccueil,
    GestionInscriptions,
    GestionInscriptionsSession,
    GestionInscriptionsCursus,
    GestionInscriptionsElearning,
    GestionSessions,
    GestionSessionsDetails,
    GestionSessionsCursusSessionDetails,
    GestionSessionsCursusElearningDetails,
    GestionCommandes,
    GestionCommandesDetails,
    GestionFactures,
    GestionInterlocuteurs,
    GestionEtablissements,
    GestionEtablissementsDetails,
    GestionAnnuaire,
    GestionSatisfaction,
    GestionContrats
  };
  const component = computed(() => {
    const rubriqueName =
      toPascalCase(props.space_id) + toPascalCase(props.sub_space_id);
    return components[rubriqueName];
  });

  const toPascalCase = (s: string) => {
    const stringArray = s.split('-');
    return stringArray
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
      .join('');
  };

  const loading: Ref<boolean> = ref(false);
  watch(
    () => props.space_id,
    async (newId) => {
      loading.value = true;
      loadSubRubrique();
      arborescenceStore.setActiveSpaceId(props.space_id);
      arborescenceStore.setActiveSubSpaceId(props.sub_space_id);
      await fetchEspaceInfo(props.space_id);
      loading.value = false;
    }
  );
  watch(
    () => props.sub_space_id,
    (newId) => {
      loading.value = true;
      arborescenceStore.setActiveSubSpaceId(newId);
      loading.value = false;
    }
  );

  const arborescenceStore = useArborescenceStore();

  const accountStore = useAccountStore();

  const entrepriseStore = useEntrepriseStore();
  const { entreprises, activeEntrepriseIds } = storeToRefs(entrepriseStore);
  const fetchUserInfo = async () => {
    await accountStore.getProfile();
    const profile = accountStore.profile;
    arborescenceStore.setSpace('gestion', 'restricted', !profile.entreprise);
    arborescenceStore.setSpace(
      'inscription',
      'restricted',
      !profile.participant
    );
  };
  const route = useRoute();
  const router = useRouter();
  const catalogueStore = useCatalogueStore();
  const fetchEspaceInfo = async (spaceId: string) => {
    const { profile } = accountStore;
    if (spaceId === 'gestion' && profile.entreprise) {
      await entrepriseStore.getProfile();
      await entrepriseStore.initActiveEntrepriseIdFromQuery(
        route.query.clientIds
      );
    }
    updateMenu();

    await catalogueStore.fetchCatalogues();
  };
  const profileLoaded = ref<boolean>(false);

  const updateMenu = () => {
    const selectedEntreprises =
      activeEntrepriseIds && activeEntrepriseIds.value.length > 0
        ? entreprises.value.filter((e) =>
            activeEntrepriseIds.value.some((a) => a === e.entrepriseId)
          )
        : entreprises.value;

    if (entreprises) {
      arborescenceStore.setSousRubrique(
        'gestion',
        'gestion',
        'contrats',
        'menuHidden',
        !(selectedEntreprises as Entreprise[]).some((e) => e.hasContrats)
      );
      arborescenceStore.setSousRubrique(
        'gestion',
        'gestion',
        'sessions',
        'menuHidden',
        !(selectedEntreprises as Entreprise[]).some((e) => e.hasSessionsIntra)
      );
    }
  };
  watch(activeEntrepriseIds, () => {
    updateMenu();
  });
  onMounted(async () => {
    loading.value = true;
    loadSubRubrique();
    arborescenceStore.setActiveSpaceId(props.space_id);
    arborescenceStore.setActiveSubSpaceId(props.sub_space_id);
    await fetchUserInfo();
    await fetchEspaceInfo(props.space_id);
    profileLoaded.value = true;
    loading.value = false;
  });
  const loadSubRubrique = () => {
    if (!props.sub_space_id) {
      initSubRubrique();
    } else {
      validateSpaceAccess();
    }
  };
  const validateSpaceAccess = () => {
    //TODO validate not restricted + validate exist subSpace + validate hasAccess
  };
  const isValidSpace = () => {
    return Object.prototype.hasOwnProperty.call(
      arborescenceStore.arborescence,
      props.space_id
    );
  };

  const initSubRubrique = () => {
    if (isValidSpace()) {
      const firstSousRubrique = arborescenceStore.getFirstSousRubrique(
        props.space_id
      );
      router.push(`/${props.space_id}/${firstSousRubrique}`);
    } else {
      router.push('/');
    }
  };
</script>

<style lang="scss" scoped></style>
